import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { format, addDays } from "date-fns";
import { Check, Star, Users, BedDouble, CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, inr } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import Stepper from "@/components/Stepper";
import { useTrip } from "@/context/TripContext";
import { TYPE_LABEL } from "@/pages/Hotels";

const ymd = (d) => format(d, "yyyy-MM-dd");

export default function HotelDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { setTrip } = useTrip();
  const [qs] = useSearchParams();
  const [h, setH] = useState(null);
  const [range, setRange] = useState(() => {
    const ci = qs.get("check_in"), co = qs.get("check_out");
    return ci && co && co > ci ? { from: new Date(`${ci}T00:00:00`), to: new Date(`${co}T00:00:00`) }
      : { from: addDays(new Date(), 1), to: addDays(new Date(), 3) };
  });
  const [rooms, setRooms] = useState(Number(qs.get("rooms")) || 1);
  const [adults, setAdults] = useState(Number(qs.get("adults")) || 2);
  const [children, setChildren] = useState(Number(qs.get("children")) || 0);
  const [avail, setAvail] = useState([]);

  useEffect(() => { api.get(`/hotels/${slug}`).then((r) => setH(r.data)).catch((e) => toast.error(formatErr(e))); }, [slug]);

  useEffect(() => {
    if (!h || !range?.from || !range?.to) return;
    api.get(`/hotels/${h.id}/availability`, { params: { check_in: ymd(range.from), check_out: ymd(range.to) } })
      .then((r) => setAvail(r.data)).catch((e) => toast.error(formatErr(e)));
    setTrip({ safari: h.name, date: `${format(range.from, "dd MMM")} – ${format(range.to, "dd MMM yyyy")}` });
  }, [h, range, setTrip]);

  if (!h) return <div className="jt-container py-20">Loading…</div>;

  const book = (rt) => {
    const p = new URLSearchParams({ kind: "stay", hotel_id: h.id, room_type_id: rt.id, check_in: ymd(range.from),
      check_out: ymd(range.to), rooms: String(rooms), adults: String(adults), children: String(children) });
    nav(`/checkout?${p}`);
  };

  return (
    <div>
      <div className="relative h-[55vh] overflow-hidden">
        <img src={h.image_url} alt={h.name} className="w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-transparent"/>
        <div className="absolute inset-x-0 bottom-0 jt-container pb-10 text-white">
          <div className="jt-eyebrow text-[#D4A359] mb-3">{TYPE_LABEL[h.property_type]} · {h.location}</div>
          <h1 className="font-display text-4xl md:text-5xl">{h.name}</h1>
          <p className="mt-3 text-[#E5E0D6] max-w-2xl flex items-center gap-2"><Star size={16} className="fill-[#D4A359] text-[#D4A359]"/>{h.rating} · {h.short_description}</p>
        </div>
      </div>

      <div className="jt-container py-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          <section>
            <h2 className="font-serif text-2xl mb-3 text-[#1E3A2B]">About the stay</h2>
            <p className="leading-relaxed">{h.description}</p>
            <div className="flex flex-wrap gap-2 mt-5">
              {h.amenities.map((a) => <span key={a} className="inline-flex items-center gap-1 bg-[#F5F0EB] rounded-full px-3 py-1.5 text-sm"><Check size={14} className="text-[#1E3A2B]"/>{a}</span>)}
            </div>
            <p className="text-sm text-[#5C6B63] mt-4">Check-in from {h.check_in_time} · Check-out by {h.check_out_time}</p>
          </section>

          <section>
            <h2 className="font-serif text-2xl mb-4 text-[#1E3A2B]">Rooms for your dates</h2>
            <div className="space-y-4">
              {avail.map((rt) => (
                <RoomRow key={rt.id} rt={rt} rooms={rooms} guests={adults + children} onBook={() => book(rt)}/>
              ))}
            </div>
          </section>

          {h.gallery?.length > 0 && (
            <section className="grid grid-cols-2 gap-3">
              {h.gallery.map((g) => <img key={g} src={g} alt="" className="rounded-xl aspect-[4/3] object-cover"/>)}
            </section>
          )}
        </div>

        <aside className="lg:sticky lg:top-24 self-start bg-white rounded-2xl ring-1 ring-black/5 p-6 shadow-xl" data-testid="stay-search-box">
          <label className="jt-eyebrow mb-2 block">Dates</label>
          <Popover>
            <PopoverTrigger asChild>
              <button className="h-11 w-full rounded-md border border-[#E2DACF] px-3 flex items-center gap-2 text-sm mb-4" data-testid="stay-dates-trigger">
                <CalendarIcon size={15} className="text-[#5C6B63]"/>
                {range?.from ? format(range.from, "dd MMM") : "Check-in"} → {range?.to ? format(range.to, "dd MMM yyyy") : "Check-out"}
              </button>
            </PopoverTrigger>
            <PopoverContent className="p-0" align="start">
              <Calendar mode="range" numberOfMonths={2} selected={range} onSelect={setRange}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}/>
            </PopoverContent>
          </Popover>
          <Stepper label="Rooms" value={rooms} setValue={setRooms} min={1} max={10} testid="stay-rooms"/>
          <Stepper label="Adults" value={adults} setValue={setAdults} min={1} testid="stay-adults"/>
          <Stepper label="Children" value={children} setValue={setChildren} testid="stay-children"/>
          <p className="text-xs text-[#5C6B63] mt-4">Pick a room on the left to continue. Want a safari too? See our <a href="/packages" className="text-[#C86D51] underline">stay + safari packages</a>.</p>
        </aside>
      </div>
    </div>
  );
}

function RoomRow({ rt, rooms, guests, onBook }) {
  const fits = guests <= rt.max_guests * rooms;
  const ok = rt.remaining_rooms >= rooms && fits;
  return (
    <div className="bg-white rounded-xl ring-1 ring-black/5 p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`room-row-${rt.id}`}>
      <div className="flex-1">
        <div className="font-serif text-lg">{rt.name}</div>
        <div className="text-sm text-[#5C6B63]">{rt.description}</div>
        <div className="flex gap-4 text-xs text-[#5C6B63] mt-2">
          <span className="flex items-center gap-1"><Users size={13}/>Up to {rt.max_guests} guests</span>
          <span className={`flex items-center gap-1 ${rt.remaining_rooms <= 2 ? "text-[#C86D51] font-semibold" : ""}`} data-testid={`room-left-${rt.id}`}><BedDouble size={13}/>{rt.remaining_rooms} left</span>
        </div>
      </div>
      <div className="text-right">
        <div className="font-serif text-xl text-[#1E3A2B]">{inr(rt.stay_price * rooms)}</div>
        <div className="text-xs text-[#5C6B63]">{inr(rt.price_per_night)}/night · {rt.nights} nights{rooms > 1 ? ` · ${rooms} rooms` : ""}</div>
      </div>
      <button disabled={!ok} onClick={onBook} data-testid={`room-book-${rt.id}`}
        className={`jt-pill-accent text-sm ${!ok ? "opacity-40 cursor-not-allowed" : ""}`}>
        {rt.remaining_rooms < rooms ? "Sold out" : !fits ? "Too many guests" : "Book stay"}
      </button>
    </div>
  );
}
